import { create } from 'zustand';
import { NotificationFeedDocument } from 'components/notification-popup/notificationFeedItem';

export interface ApplyFeedPagePayload {
  requestId: number;
  docs: NotificationFeedDocument[];
  page: number;
  pages: number;
  total: number;
  isLoadMore: boolean;
}

export interface ApplyFeedRefreshPayload {
  requestId: number;
  docs: NotificationFeedDocument[];
  pages: number;
  total: number;
}

export interface NotificationsState {
  feed: NotificationFeedDocument[];
  page: number;
  pages: number;
  total: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  /**
   * A background refresh is in flight. Never rendered: its whole point is to
   * update the list without a spinner. It exists so the scroll guard and the
   * queued-refresh effect can see it, because only one feed request may be in
   * flight at a time. Two racing requests is what let an older response land
   * on top of a newer one.
   */
  isRefreshing: boolean;
  isError: boolean;
  /**
   * Whether the request that raised isError was a load-more. Cached rows can
   * survive a close now, so "there are rows on screen" no longer implies the
   * failure was a load-more, and the retry has to know which page to ask for.
   */
  errorIsLoadMore: boolean;
  /**
   * False until a request has settled, either way. The mount effect fetches
   * after the first paint, so without this the tray would render its empty
   * state for one frame against a store that has not been asked yet.
   */
  hasLoaded: boolean;
  /**
   * The admin the cached feed belongs to. Logout and login are both
   * client-side navigations in this app, with no document reload, so this
   * store survives an account change in the same tab. Without this guard the
   * next admin would be shown the previous admin's notifications until their
   * own first response landed.
   */
  ownerId?: string;
  /**
   * Bumped by closeFeed, openFeedFor on an owner change, and markAllReadLocally.
   * It cancels, so it is deliberately NOT bumped per request: requests are
   * serialised instead, and cancelling one that is still wanted is what
   * stranded the tray on a spinner. A feed request carries the value
   * it saw when it started, and the write actions drop the result if it no
   * longer matches. Without it a late response repopulates a dropdown the user
   * has already closed, and a late load-more overwrites a newer page 1.
   */
  requestId: number;
}

export interface NotificationsStore extends NotificationsState {
  beginFeedRequest(isLoadMore: boolean): number;
  beginFeedRefresh(): number;
  endFeedRefresh(): void;
  applyFeedPage(payload: ApplyFeedPagePayload): void;
  applyFeedError(payload: { requestId: number; isLoadMore?: boolean }): void;
  applyFeedRefresh(payload: ApplyFeedRefreshPayload): void;
  markReadLocally(notificationId: string): void;
  markAllReadLocally(): void;
  openFeedFor(ownerId?: string): void;
  closeFeed(): void;
}

export const initialNotificationsState: NotificationsState = {
  feed: [],
  page: 1,
  pages: 0,
  total: 0,
  isLoading: false,
  isLoadingMore: false,
  isRefreshing: false,
  isError: false,
  errorIsLoadMore: false,
  hasLoaded: false,
  ownerId: undefined,
  requestId: 0,
};

const useNotificationsStore = create<NotificationsStore>((set, get) => ({
  ...initialNotificationsState,

  beginFeedRequest: (isLoadMore: boolean) => {
    set(state => ({
      isLoading: !isLoadMore,
      isLoadingMore: isLoadMore,
      isError: false,
      requestId: state.requestId,
    }));

    return get().requestId;
  },

  /**
   * Marks a background refresh in flight without touching the loading flags,
   * so no spinner appears over rows on screen. It claims no new requestId:
   * the caller guarantees nothing else is in flight, so there is nothing to
   * outrank and nothing to cancel.
   */
  beginFeedRefresh: () => {
    set(() => ({ isRefreshing: true }));

    return get().requestId;
  },

  endFeedRefresh: () => set(() => ({ isRefreshing: false })),

  /**
   * Commits a page of the feed. Deduplicates by `_id` because a notification
   * arriving between two requests shifts the page boundary, so page 2 can
   * legitimately repeat a row already shown on page 1.
   */
  applyFeedPage: ({ requestId, docs, page, pages, total, isLoadMore }: ApplyFeedPagePayload) =>
    set(state => {
      if (state.requestId !== requestId) {
        // Cancelled, so it changes nothing at all. It must not mark the tray
        // loaded, because this epoch has not heard back yet, and it must not
        // clear the loading flags, because those now belong to the request
        // that replaced it. Every action that bumps requestId (closeFeed,
        // openFeedFor on an owner change, markAllReadLocally) already clears
        // the flags itself, so there is nothing left here to tidy up.
        return state;
      }

      const existing = isLoadMore ? state.feed : [];
      const seen = new Set(existing.map(item => String(item?._id)));
      const appended = docs.filter(item => {
        const id = String(item?._id);

        if (seen.has(id)) {
          return false;
        }

        seen.add(id);

        return true;
      });

      return {
        ...state,
        feed: [...existing, ...appended],
        page,
        pages,
        total,
        isLoading: false,
        isLoadingMore: false,
        isError: false,
        errorIsLoadMore: false,
        hasLoaded: true,
      };
    }),

  /** Same staleness guard as applyFeedPage: a request that failed after the
   * dropdown closed must not raise the error state for the next open. */
  applyFeedError: ({ requestId, isLoadMore = false }: { requestId: number; isLoadMore?: boolean }) =>
    set(state => {
      if (state.requestId !== requestId) {
        return state;
      }

      return {
        ...state,
        isLoading: false,
        isLoadingMore: false,
        isError: true,
        errorIsLoadMore: isLoadMore,
        hasLoaded: true,
      };
    }),

  /**
   * Merges a page-1 poll into a list the reader may already have paginated.
   * Only ids the feed does not hold are prepended and `page` is left alone, so
   * a new arrival appears at the top without rewinding pagination or moving
   * the reader's scroll position. The loading and error flags are untouched on
   * purpose: this runs in the background behind a populated tray, so it must
   * never raise a spinner over rows that are already on screen, and a failure
   * is not worth replacing a good list with an error.
   *
   * Additive only. A notification read or deleted in another session stays
   * until the dropdown is closed and reopened.
   */
  applyFeedRefresh: ({ requestId, docs, pages, total }: ApplyFeedRefreshPayload) =>
    set(state => {
      if (state.requestId !== requestId) {
        return state;
      }

      const held = new Set(state.feed.map(item => String(item?._id)));
      const arrived = docs.filter(item => {
        const id = String(item?._id);

        if (held.has(id)) {
          return false;
        }

        held.add(id);

        return true;
      });

      return { ...state, feed: [...arrived, ...state.feed], pages, total, hasLoaded: true };
    }),

  markReadLocally: (notificationId: string) =>
    set(state => ({
      ...state,
      feed: state.feed.map(item =>
        String(item?._id) === String(notificationId) && item.isNotificationRead !== true
          ? { ...item, isNotificationRead: true }
          : item
      ),
    })),

  markAllReadLocally: () =>
    set(state => ({
      ...state,
      feed: state.feed.map(item => (item.isNotificationRead === true ? item : { ...item, isNotificationRead: true })),
      // Pending pages were selected before the mark-all and still carry unread
      // values, so anything already in flight has to be discarded.
      requestId: state.requestId + 1,
      isLoading: false,
      isLoadingMore: false,
    })),

  /**
   * Called when the dropdown opens. Keeps the cache for the same admin so the
   * rows paint immediately while the fresh page 1 is fetched, and wipes it
   * outright for a different one. requestId is bumped on a wipe so a response
   * still in flight for the previous admin cannot land afterwards.
   */
  openFeedFor: (ownerId?: string) =>
    set(state => {
      if (state.ownerId === ownerId) {
        return { ...state, ownerId };
      }

      return { ...initialNotificationsState, ownerId, requestId: state.requestId + 1 };
    }),

  /**
   * Called when the dropdown closes. The list is deliberately kept: reopening
   * paints it at once and the refetch swaps in fresh rows underneath, which is
   * what the legacy socket-backed dropdown did by leaving its data in the
   * socket store. Only `page` is rewound, because the next open refetches page
   * 1. Bumping requestId makes any request from the open that just ended a
   * no-op, so it cannot write into the next one.
   */
  closeFeed: () =>
    set(state => ({
      ...state,
      page: 1,
      isLoading: false,
      isLoadingMore: false,
      isRefreshing: false,
      isError: false,
      errorIsLoadMore: false,
      requestId: state.requestId + 1,
    })),
}));

export default useNotificationsStore;
